import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Button,
  Box,
  styled,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Divider,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Chip
} from '@mui/material';
import AccountingDataTable from '../accounting/shared/AccountingDataTable';
import type { DataTableColumn } from '../accounting/shared/AccountingDataTable';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getPersonnelByUid } from '../../services/migration/personnelDataService';
import { getBranchById } from '../../services/migration/branchDataService';
import { getPersonnelTransactions, getPersonnelBalances, PersonnelBalance } from '../../services/migration/personnelTransactionService';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import AddCardIcon from '@mui/icons-material/AddCard';
import PaymentIcon from '@mui/icons-material/Payment';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import SendIcon from '@mui/icons-material/Send';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// Yeni Modallar
import AddReceivableModal from './components/AddReceivableModal';
import AddDebtModal from './components/AddDebtModal';
import SalaryBonusModal from './components/SalaryBonusModal';
import PaymentModal from './components/PaymentModal';
import PersonnelTransactionDeleteConfirmModal, { DeletableTransaction } from './components/PersonnelTransactionDeleteConfirmModal';
import { useAuth } from '../../contexts/AuthContext'; // companyId için eklendi
import { useTranslation } from 'react-i18next';
import { AppDatePicker } from '../common/AppDatePicker';

// Accounts.tsx'den alınan HeaderContainer tanımı
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  padding: theme.spacing(0, 2, 0, 2), // Sağ ve sol padding theme.spacing(2) olarak güncellendi
}));

// Örnek işlem tipi, bu daha sonra dinamikleşebilir
interface Transaction {
  id: string;
  amount: number;
  createdAt: string;
  currency: string;
  description: string;
  isAccrual: boolean;
  paymentMethod: string;
  performedBy: string;
  personnelId: string;
  personnelName: string;
  transactionType: TransactionType;
  cashboxId?: string | null;
  bankAccountId?: string | null;
  cashboxName?: string;
  bankName?: string;
  // Hesaplanan alanlar: yürüyen bakiyenin işlem öncesi/sonrası değerleri
  balance?: number;
  previousBalance?: number;
  nextBalance?: number;
}

type TransactionType = 'DEBT' | 'RECEIVABLE' | 'PAYMENT' | 'COLLECTION';

const TRANSACTION_TYPES: TransactionType[] = ['DEBT', 'RECEIVABLE', 'PAYMENT', 'COLLECTION'];

interface PersonnelActionsProps {
  /** Verilirse route parametresi yerine kullanılır (yönetici panelindeki iç görünüm). */
  personnelId?: string;
  onBack?: () => void;
  /** Şube yöneticisi: kasa/banka listeleri managerBranchId'ye süzülür, proje seçimi gizlenir. */
  isManager?: boolean;
  managerBranchId?: string;
}

const PersonnelActions: React.FC<PersonnelActionsProps> = ({
  personnelId: personnelIdProp,
  onBack,
  isManager = false,
  managerBranchId,
}) => {
  const { i18n } = useTranslation();
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const params = useParams<{ personnelId: string }>();
  const personnelId = personnelIdProp ?? params.personnelId;
  const modalScopeProps = isManager
    ? { restrictToBranchId: managerBranchId ?? '', hideProject: true }
    : {};
  const navigate = useNavigate();
  const { userDetails } = useAuth(); // userDetails.companyId için
  const [personnelName, setPersonnelName] = useState<string | null>(null);
  const [branchName, setBranchName] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingExcel, setLoadingExcel] = useState<boolean>(false);

  // Tek 3 nokta işlem menüsü (Cashbox/Accounts ile aynı yapı)
  const [anchorElActions, setAnchorElActions] = React.useState<null | HTMLElement>(null);
  const handleOpenActionsMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorElActions(event.currentTarget);
  const handleCloseActionsMenu = () => setAnchorElActions(null);

  // Filtre görünürlüğü (Cashbox/Accounts ile aynı — filtre ikonuyla açılır)
  const [showFilters, setShowFilters] = useState(false);

  // Tarih ve işlem türü filtreleri için state
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState<string>('');
  const [filteredTransactions, setFilteredTransactions] = useState<Transaction[]>([]);
  const [isFiltered, setIsFiltered] = useState(false);

  // Tekil hareket detay modalı
  const [transactionForDetails, setTransactionForDetails] = useState<Transaction | null>(null);

  // Tablo için state (sayfalama ortak AccountingDataTable içinde)
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [balances, setBalances] = useState<PersonnelBalance[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState<boolean>(false);

  // Yeni modal state'leri
  const [openAddReceivableModal, setOpenAddReceivableModal] = useState(false);
  const [openAddDebtModal, setOpenAddDebtModal] = useState(false);
  const [openSalaryBonusModal, setOpenSalaryBonusModal] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [modalSourceAction, setModalSourceAction] = useState<'debt_receivable' | 'payment_collection' | null>(null);
  const [salaryBonusType, setSalaryBonusType] = useState<'salary' | 'bonus' | null>(null);
  const [paymentModalType, setPaymentModalType] = useState<'salary' | 'bonus' | 'advance' | null>(null);
  const [paymentDescription, setPaymentDescription] = useState<string>('');
  // 'Avans Ver' akışında ödeme ADVANCE olarak işaretlenir (maaş oluştururken avans uyarısı için)
  const [paymentKind, setPaymentKind] = useState<'ADVANCE' | undefined>(undefined);

  // Hareket silme onay modalı
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState<DeletableTransaction | null>(null);

  // Yeni modal açma/kapama fonksiyonları
  const handleOpenAddReceivableModal = (source: 'debt_receivable' | 'payment_collection') => {
    setModalSourceAction(source);
    setOpenAddReceivableModal(true);
    handleCloseActionsMenu();
  };

  const handleOpenAddDebtModal = (source: 'debt_receivable' | 'payment_collection') => {
    setModalSourceAction(source);
    setPaymentDescription(''); // Diğer modallarda açıklama alanını temizle
    setPaymentKind(undefined); // Avans dışı akışlarda işaret temizlenir
    setOpenAddDebtModal(true);
    handleCloseActionsMenu();
  };

  // Maaş/Prim modalını açma fonksiyonu
  const handleOpenSalaryBonusModal = (type: 'salary' | 'bonus') => {
    setSalaryBonusType(type);
    setOpenSalaryBonusModal(true);
    handleCloseActionsMenu();
  };

  // Ödeme modalını açma fonksiyonu
  const handleOpenPaymentModal = (type: 'salary' | 'bonus' | 'advance') => {
    setPaymentModalType(type);
    setOpenPaymentModal(true);
    handleCloseActionsMenu();
  };

  const transactionTypeLabel = (type: string) => {
    switch (type) {
      case 'DEBT': return tr('Şirket Alacağı (Personel Borcu)', 'Company Receivable (Personnel Debt)');
      case 'RECEIVABLE': return tr('Şirket Borcu (Personel Alacağı)', 'Company Payable (Personnel Receivable)');
      case 'PAYMENT': return tr('Ödeme', 'Payment');
      case 'COLLECTION': return tr('Tahsilat', 'Collection');
      default: return type;
    }
  };

  const paymentMethodLabel = (method: string) => {
    switch (method) {
      case 'accrual': return tr('Tahakkuk', 'Accrual');
      case 'cash': return tr('Nakit', 'Cash');
      case 'bank': return tr('Banka', 'Bank');
      case 'maaş': return tr('Maaş', 'Salary');
      case 'pirim': return tr('Prim', 'Bonus');
      default: return method;
    }
  };

  /** Hareketin bağlı olduğu kasa/banka; tahakkuklarda para hareketi yoktur. */
  const bankOrCashLabel = (row: Transaction) => {
    if (row.bankName) return row.bankName;
    if (row.cashboxName) return row.cashboxName;
    if (row.isAccrual) {
      if (row.description?.includes('maaş hakedişi')) return tr('Maaş', 'Salary');
      if (row.description?.includes('prim hakedişi')) return tr('Prim', 'Bonus');
      return tr('Tahakkuk', 'Accrual');
    }
    return '-';
  };

  const formatAmount = (value: number, currency?: string) =>
    new Intl.NumberFormat(isEnglish ? 'en-US' : 'tr-TR', { style: 'currency', currency: currency || 'TRY' }).format(value);

  const formatDateTime = (value: string) => {
    const date = new Date(value);
    return date.toLocaleDateString(isEnglish ? 'en-US' : 'tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })
      + ' ' + date.toLocaleTimeString(isEnglish ? 'en-US' : 'tr-TR', { hour: '2-digit', minute: '2-digit' });
  };

  /** Şirket perspektifi: DEBT/PAYMENT bakiyeyi artırır, RECEIVABLE/COLLECTION azaltır. */
  const isCompanyReceivable = (type: string) => type === 'DEBT' || type === 'PAYMENT';

  // Personel hareketlerini çekme fonksiyonu (SQL — gts_accounting_api PersonnelTransaction)
  const fetchPersonnelTransactions = async () => {
    if (!personnelId || !userDetails?.companyId) return;

    setLoadingTransactions(true);
    try {
      const [{ data }, balanceRows] = await Promise.all([
        getPersonnelTransactions(personnelId, 1, 500),
        getPersonnelBalances(personnelId),
      ]);

      // Kronolojik (eskiden yeniye) sirala, PARA-BIRIMI-BAZLI yuruyen bakiye hesapla
      const asc = [...data].sort(
        (a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
      );
      const running: Record<string, number> = {};
      const withBalance: Transaction[] = asc.map((t) => {
        const cur = t.currency || 'TRY';
        // Tablodaki TUM hareketler bakiyeye sayilir (tahakkuklar dahil) —
        // backend GetBalance/GetAllBalances ile ayni mantik.
        const delta = isCompanyReceivable(t.transactionType) ? t.amount : -t.amount;
        const previousBalance = running[cur] || 0;
        running[cur] = previousBalance + delta;
        return {
          id: t.id,
          amount: t.amount,
          createdAt: t.createdAt,
          currency: cur,
          description: t.description,
          isAccrual: t.isAccrual,
          paymentMethod: t.paymentMethod,
          performedBy: t.performedBy,
          personnelId: t.personnelId,
          personnelName: t.personnelName,
          transactionType: t.transactionType,
          cashboxId: t.cashboxId,
          bankAccountId: t.bankAccountId,
          cashboxName: t.cashboxName,
          bankName: t.bankName,
          balance: running[cur],
          previousBalance,
        };
      });

      // Sonraki bakiye: ayni para biriminde kronolojik olarak bir SONRAKI hareketin
      // bakiyesi. Detay modalinda "bu hareketten sonra ne oldu" sorusunu yanitlar.
      for (let i = 0; i < withBalance.length; i++) {
        const next = withBalance.slice(i + 1).find((x) => x.currency === withBalance[i].currency);
        withBalance[i].nextBalance = next?.balance;
      }

      // En yeni en ustte
      withBalance.reverse();
      setTransactions(withBalance);
      setBalances(balanceRows);
    } catch (error) {
      console.error("Personel hareketleri alınırken hata:", error);
      setTransactions([]);
      setBalances([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    const fetchPersonnelData = async () => {
      setLoading(true);
      setPersonnelName(null); 
      setBranchName(null);  

      if (!personnelId) {
        setPersonnelName(tr("Personel ID bulunamadı.", "Personnel ID not found."));
        setLoading(false);
        return;
      }

      // ✅ companyId userDetails prop'undan veya AuthContext cache'inden - Firebase sorgusu YOK!
      let companyIdToUse = userDetails?.companyId || null;

      if (!companyIdToUse) {
        setPersonnelName(tr("Oturum açmış kullanıcının şirket bilgisi bulunamadı.", "Signed-in user's company information was not found."));
        setLoading(false);
        return;
      }

      let finalPersonnelName: string | null = null;
      let finalBranchesId: string | null = null;

      try {
        // ✅ SQL: personel bilgisi (gts_hr_api → GET /api/hr/personnel/{uid})
        const personnel = await getPersonnelByUid(personnelId, companyIdToUse);

        if (personnel) {
          finalPersonnelName = personnel.name || tr('İsimsiz Personel', 'Unnamed Personnel');
          finalBranchesId = (personnel.branchesId as string | null) || null;
        } else {
          finalPersonnelName = tr('Personel bulunamadı.', 'Personnel not found.');
        }

        setPersonnelName(finalPersonnelName);

        if (finalBranchesId) {
          // ✅ SQL: şube bilgisi (mainApi → GET /api/Branch)
          const branch = await getBranchById(companyIdToUse, finalBranchesId);
          setBranchName(branch?.name || tr('Atanmış şube kaydı bulunamadı.', 'Assigned branch record not found.'));
        } else {
          setBranchName(null);
        }

      } catch (error) {
        console.error("Personel verileri alınırken hata: ", error);
        setPersonnelName(finalPersonnelName || tr('Personel bilgileri yüklenirken bir hata oluştu.', 'An error occurred while loading personnel info.'));
        setBranchName(null);
      } finally {
        setLoading(false);
      }
    };

    fetchPersonnelData();
  }, [personnelId]);
  
  // Personel bilgileri yüklendiğinde hareketleri çek
  useEffect(() => {
    if (personnelId && userDetails?.companyId && !loading) {
      fetchPersonnelTransactions();
    }
  }, [personnelId, userDetails?.companyId, loading]);

  const handleGoBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    // Browser history'den bir önceki sayfaya git
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // History yoksa varsayılan sayfaya git
      navigate('/personnel');
    }
  };

  const hasFilterInput = !!startDate || !!endDate || !!transactionTypeFilter;

  const handleFilter = () => {
    if (!hasFilterInput) {
      // Hiçbir filtre girilmediyse orijinal listeyi göster
      setFilteredTransactions([]);
      setIsFiltered(false);
      return;
    }

    const startTimestamp = startDate ? new Date(startDate).getTime() : 0;
    const endTimestamp = endDate ? new Date(endDate + 'T23:59:59').getTime() : Number.MAX_SAFE_INTEGER;

    const filtered = transactions.filter(transaction => {
      const transactionDate = new Date(transaction.createdAt).getTime();
      const inDateRange = transactionDate >= startTimestamp && transactionDate <= endTimestamp;
      const matchesType = !transactionTypeFilter || transaction.transactionType === transactionTypeFilter;
      return inDateRange && matchesType;
    });

    setFilteredTransactions(filtered);
    setIsFiltered(true);
  };

  const handleClearFilter = () => {
    setStartDate('');
    setEndDate('');
    setTransactionTypeFilter('');
    setFilteredTransactions([]);
    setIsFiltered(false);
  };
  
  // Gösterilecek işlemler (filtrelenmiş veya tüm liste)
  const displayedTransactions = isFiltered ? filteredTransactions : transactions;

  // Toplam değerleri hesapla
  const calculateTotals = () => {
    let totalDebit = 0;
    let totalCredit = 0;

    displayedTransactions.forEach(transaction => {
      if (isCompanyReceivable(transaction.transactionType)) {
        totalDebit += transaction.amount;
      } else {
        totalCredit += transaction.amount;
      }
    });

    return { totalDebit, totalCredit };
  };

  const { totalDebit, totalCredit } = calculateTotals();

  const handleExportExcel = async () => {
    try {
      setLoadingExcel(true);
      
      // Excel workbook oluştur
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(tr('Personel İşlemleri', 'Personnel Transactions'));
      
      // Sütunları tanımla
      worksheet.columns = [
        { header: tr('İşlem Tarihi', 'Transaction Date'), key: 'createdAt', width: 20 },
        { header: tr('İşlem Türü', 'Transaction Type'), key: 'transactionType', width: 30 },
        { header: tr('Ödeme Tipi', 'Payment Method'), key: 'paymentMethod', width: 15 },
        { header: tr('Açıklama', 'Description'), key: 'description', width: 40 },
        { header: tr('Şirket Alacağı', 'Company Receivable'), key: 'debit', width: 15 },
        { header: tr('Şirket Borcu', 'Company Payable'), key: 'credit', width: 15 },
        { header: tr('Bakiye', 'Balance'), key: 'balance', width: 15 },
        { header: tr('Banka/Kasa', 'Bank/Cashbox'), key: 'bankOrCash', width: 20 },
        { header: tr('İşlemi Yapan', 'Processed By'), key: 'performedBy', width: 20 },
      ];
      
      // Stil tanımlamaları
      const headerStyle = {
        font: { bold: true, color: { argb: 'FFFFFFFF' } },
        fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF4F81BD' } },
        border: {
          top: { style: 'thin' as const },
          left: { style: 'thin' as const },
          bottom: { style: 'thin' as const },
          right: { style: 'thin' as const }
        }
      };
      
      // Başlık stilini uygula
      worksheet.getRow(1).eachCell(cell => {
        cell.style = headerStyle;
      });
      
      // İşlemleri Excel'e aktar
      displayedTransactions.forEach(transaction => {
        const isReceivable = isCompanyReceivable(transaction.transactionType);

        worksheet.addRow({
          createdAt: formatDateTime(transaction.createdAt),
          transactionType: transactionTypeLabel(transaction.transactionType),
          paymentMethod: paymentMethodLabel(transaction.paymentMethod),
          description: transaction.description,
          debit: isReceivable && transaction.amount > 0 ? transaction.amount : null,
          credit: !isReceivable && transaction.amount > 0 ? transaction.amount : null,
          balance: transaction.balance,
          bankOrCash: bankOrCashLabel(transaction),
          performedBy: transaction.performedBy
        });
      });
      
      // Toplam satırı ekle
      const totalRow = worksheet.addRow({
        createdAt: '',
        transactionType: '',
        paymentMethod: '',
        description: tr('GENEL TOPLAM:', 'GRAND TOTAL:'),
        debit: totalDebit,
        credit: totalCredit,
        balance: '',
        bankOrCash: '',
        performedBy: ''
      });
      
      // Toplam satırı stilleri
      totalRow.eachCell((cell, colNumber) => {
        cell.font = { bold: true };
        if (colNumber === 5) { // Debit (Şirket Alacağı) hücresi
          cell.fill = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFE2EFDA' } };
        } else if (colNumber === 6) { // Credit (Şirket Borcu) hücresi
          cell.fill = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FFFCE4D6' } };
        }
      });
      
      // Zebrastripe (alternatif satır renklendirme)
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1 && rowNumber < worksheet.rowCount) { // Başlık ve toplam satırı hariç
          const fillColor = rowNumber % 2 === 0 ? 'FFF2F2F2' : 'FFFFFFFF';
          row.eachCell(cell => {
            cell.style = {
              ...cell.style,
              fill: { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: fillColor } },
              border: {
                top: { style: 'thin' as const },
                left: { style: 'thin' as const },
                bottom: { style: 'thin' as const },
                right: { style: 'thin' as const }
              }
            };
          });
        }
      });
      
      // Para birimi biçimlendirmesi
      worksheet.getColumn('debit').numFmt = '#,##0.00 ₺';
      worksheet.getColumn('credit').numFmt = '#,##0.00 ₺';
      worksheet.getColumn('balance').numFmt = '#,##0.00 ₺';
      
      // Dosya adına tarih ve personel adı ekle
      const currentDate = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const safePersonnelName = personnelName ? personnelName.replace(/[^a-zA-Z0-9_]/g, '_') : tr('Personel', 'Personnel');
      const fileName = `${safePersonnelName}_${tr('Hareketler', 'Transactions')}_${currentDate}.xlsx`;
      
      // Excel dosyasını oluştur ve indir
      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), fileName);
      
      console.log(`${displayedTransactions.length} adet personel hareketi Excel dosyasına aktarıldı.`);
      
    } catch (error) {
      console.error('Excel dışa aktarma hatası:', error);
    } finally {
      setLoadingExcel(false);
    }
  };

  // Ortak tablo (AccountingDataTable) için kolon tanımları — eski özel tablo ile birebir
  const dataColumns: DataTableColumn<Transaction>[] = [
    {
      id: 'createdAt',
      label: tr('İşlem Tarihi', 'Transaction Date'),
      width: '150px',
      render: (row) => formatDateTime(row.createdAt),
    },
    {
      id: 'transactionType',
      label: tr('İşlem Türü', 'Transaction Type'),
      render: (row) => transactionTypeLabel(row.transactionType),
    },
    {
      id: 'paymentMethod',
      label: tr('Ödeme Tipi', 'Payment Method'),
      render: (row) => paymentMethodLabel(row.paymentMethod),
    },
    {
      id: 'description',
      label: tr('Açıklama', 'Description'),
      field: 'description',
      tooltip: true,
    },
    {
      id: 'debit',
      label: tr('Şirket Alacağı', 'Company Receivable'),
      align: 'right',
      render: (row) => (isCompanyReceivable(row.transactionType) && row.amount > 0 ? formatAmount(row.amount, row.currency) : '-'),
    },
    {
      id: 'credit',
      label: tr('Şirket Borcu', 'Company Payable'),
      align: 'right',
      render: (row) => (!isCompanyReceivable(row.transactionType) && row.amount > 0 ? formatAmount(row.amount, row.currency) : '-'),
    },
    {
      id: 'balance',
      label: tr('Bakiye', 'Balance'),
      align: 'right',
      render: (row) => formatAmount(row.balance ?? 0, row.currency),
    },
    {
      id: 'bankOrCash',
      label: tr('Banka/Kasa', 'Bank/Cashbox'),
      render: (row) => bankOrCashLabel(row),
    },
    {
      id: 'performedBy',
      label: tr('İşlemi Yapan', 'Processed By'),
      field: 'performedBy',
    },
    {
      id: 'actions',
      label: tr('İşlem', 'Action'),
      align: 'center',
      width: '70px',
      render: (row) => (
        <Tooltip title={tr('Hareketi Sil', 'Delete Transaction')}>
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              setTransactionToDelete({
                id: row.id,
                amount: row.amount,
                createdAt: row.createdAt,
                currency: row.currency,
                description: row.description,
                isAccrual: row.isAccrual,
                paymentMethod: row.paymentMethod,
                performedBy: row.performedBy,
                transactionType: row.transactionType,
                cashboxName: row.cashboxName,
                bankName: row.bankName,
              });
              setDeleteModalOpen(true);
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  if (loading) {
    // Yükleme durumu için basit bir mesaj, HeaderContainer'a sığacak şekilde
    return (
      <HeaderContainer sx={{ pt:1, justifyContent: 'center' }}>
         <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>{tr('Yükleniyor...', 'Loading...')}</Typography>
      </HeaderContainer>
    );
  }

  return (
    <>
      <HeaderContainer sx={{ pt:1 }}> {/* Ana sarmalayıcı olarak HeaderContainer, alignItems default center olduğu için butonlar ve isim dikeyde ortalanacaktır*/} 
        <Box> 
          <Button 
            variant="outlined" 
            startIcon={<ArrowBackIcon />} 
            onClick={handleGoBack} 
            sx={{ mr: 2, mb: { xs: 1, sm: 0 } }} // İsimle yan yana gelince sağa biraz boşluk, küçük ekranlarda altına gelirse alta boşluk
          >
            {tr('Geri', 'Back')}
          </Button>
          <Typography variant="h5" component="span" sx={{ fontWeight: 'bold', verticalAlign: 'middle' }}>
            {personnelName}
          </Typography>
          {branchName && (
            <Typography variant="subtitle1" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', ml: 1, verticalAlign: 'middle' }}>
              <BusinessIcon fontSize="small" sx={{ mr: 0.5 }} /> 
              {branchName}
            </Typography>
          )}
        </Box>

        {/* Sağ üst: filtre + 3 nokta menüsü (Cashbox/Accounts ile aynı yapı) */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <IconButton
            color="primary"
            onClick={() => setShowFilters(!showFilters)}
            sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            <FilterListIcon />
          </IconButton>
          <IconButton
            color="primary"
            onClick={handleOpenActionsMenu}
            sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
            aria-label={tr('İşlemler', 'Actions')}
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorElActions}
            open={Boolean(anchorElActions)}
            onClose={handleCloseActionsMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <ListSubheader sx={{ lineHeight: '32px' }}>{tr('Borç / Alacak Ekle', 'Add Debt / Receivable')}</ListSubheader>
            <MenuItem onClick={() => handleOpenAddDebtModal('debt_receivable')}>
              <ListItemIcon><AddCardIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{tr('Borç Ekle', 'Add Debt')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleOpenAddReceivableModal('debt_receivable')}>
              <ListItemIcon><AddCardIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{tr('Alacak Ekle', 'Add Receivable')}</ListItemText>
            </MenuItem>
            <Divider />
            <ListSubheader sx={{ lineHeight: '32px' }}>{tr('Ödeme / Tahsilat Ekle', 'Add Payment / Collection')}</ListSubheader>
            <MenuItem onClick={() => handleOpenAddDebtModal('payment_collection')}>
              <ListItemIcon><PaymentIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{tr('Ödeme Ekle', 'Add Payment')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleOpenAddReceivableModal('payment_collection')}>
              <ListItemIcon><PaymentIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{tr('Tahsilat Ekle', 'Add Collection')}</ListItemText>
            </MenuItem>
            <Divider />
            <ListSubheader sx={{ lineHeight: '32px' }}>{tr('Maaş / Prim Oluştur', 'Create Salary / Bonus')}</ListSubheader>
            <MenuItem onClick={() => handleOpenSalaryBonusModal('salary')}>
              <ListItemIcon><PriceCheckIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{tr('Maaş Oluştur', 'Create Salary')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleOpenSalaryBonusModal('bonus')}>
              <ListItemIcon><PriceCheckIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{tr('Prim Oluştur', 'Create Bonus')}</ListItemText>
            </MenuItem>
            <Divider />
            <ListSubheader sx={{ lineHeight: '32px' }}>{tr('Ödeme Yap', 'Make Payment')}</ListSubheader>
            <MenuItem onClick={() => handleOpenPaymentModal('salary')}>
              <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{tr('Maaş Ödemesi Yap', 'Pay Salary')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => handleOpenPaymentModal('bonus')}>
              <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{tr('Prim Ödemesi Yap', 'Pay Bonus')}</ListItemText>
            </MenuItem>
            <MenuItem onClick={() => {
              setModalSourceAction('payment_collection');
              setPaymentDescription(isEnglish ? `Advance payment for ${personnelName}` : `${personnelName} için avans`);
              setPaymentKind('ADVANCE');
              setOpenAddDebtModal(true);
              handleCloseActionsMenu();
            }}>
              <ListItemIcon><SendIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{tr('Avans Ver', 'Give Advance')}</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem
              onClick={() => { handleCloseActionsMenu(); handleExportExcel(); }}
              disabled={loadingExcel || transactions.length === 0}
            >
              <ListItemIcon><FileDownloadIcon fontSize="small" /></ListItemIcon>
              <ListItemText>{loadingExcel ? tr('İndiriliyor...', 'Downloading...') : tr('Excel İndir', 'Download Excel')}</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </HeaderContainer>

      {/* Tarih Filtreleri ve Tablo Alanı */}
      <Box sx={{ px: 3, mt: 2 }}>
        {showFilters && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <AppDatePicker
            label={tr('Başlangıç Tarihi', 'Start Date')}
            value={startDate}
            onChange={setStartDate}
            sx={{ maxWidth: 180 }}
            size="small"
          />
          <AppDatePicker
            label={tr('Bitiş Tarihi', 'End Date')}
            value={endDate}
            onChange={setEndDate}
            sx={{ maxWidth: 180 }}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="personnel-tx-type-filter-label">{tr('İşlem Türü', 'Transaction Type')}</InputLabel>
            <Select
              labelId="personnel-tx-type-filter-label"
              value={transactionTypeFilter}
              label={tr('İşlem Türü', 'Transaction Type')}
              onChange={(e) => setTransactionTypeFilter(e.target.value)}
            >
              <MenuItem value=""><em>{tr('Tümü', 'All')}</em></MenuItem>
              {TRANSACTION_TYPES.map((type) => (
                <MenuItem key={type} value={type}>{transactionTypeLabel(type)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box>
            <Button 
              variant="outlined" 
              onClick={handleFilter} 
              startIcon={<FilterListIcon />}
              size="small"
              sx={{ mr: 1 }}
              disabled={!hasFilterInput}
            >
              {tr('Filtrele', 'Filter')}
            </Button>
            {isFiltered && (
              <Button 
                variant="outlined" 
                onClick={handleClearFilter} 
                size="small"
                color="secondary"
              >
                {tr('Filtreyi Temizle', 'Clear Filter')}
              </Button>
            )}
          </Box>
          {isFiltered && (
            <Typography variant="body2" color="text.secondary">
              {isEnglish
                ? `Filter active: showing ${filteredTransactions.length} / ${transactions.length} records`
                : `Filtre aktif: ${filteredTransactions.length} / ${transactions.length} kayıt görüntüleniyor`}
            </Typography>
          )}
        </Box>
        )}

        {/* Toplam bakiye bilgisi — PARA BIRIMI BAZLI */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="h6" component="div" sx={{ mb: 0.5 }}>
              {tr('Güncel Bakiye:', 'Current Balance:')}
            </Typography>
            {balances.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {tr('Bakiye sıfır', 'Balance is zero')}
              </Typography>
            ) : (
              balances.map((b) => (
                <Box key={b.currency} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box component="span" sx={{
                    color: b.balance > 0 ? 'success.main' : b.balance < 0 ? 'error.main' : 'text.primary',
                    fontWeight: 'bold',
                    minWidth: 140,
                  }}>
                    {new Intl.NumberFormat(isEnglish ? 'en-US' : 'tr-TR', { style: 'currency', currency: b.currency || 'TRY' }).format(b.balance)}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {b.balance > 0
                      ? tr('Şirketin alacağı var', 'Company has receivables')
                      : b.balance < 0
                        ? tr('Şirketin borcu var', 'Company has payables')
                        : tr('Bakiye sıfır', 'Balance is zero')}
                  </Typography>
                </Box>
              ))
            )}
          </Box>
        </Box>

        <AccountingDataTable<Transaction>
          columns={dataColumns}
          rows={displayedTransactions}
          getRowId={(row) => row.id}
          loading={loadingTransactions}
          defaultRowsPerPage={50}
          rowsPerPageOptions={[10, 25, 50, 100]}
          emptyMessage={isFiltered ? tr('Seçilen filtreye uyan işlem bulunmuyor.', 'No transactions match the selected filter.') : tr('Personel için işlem bulunmuyor.', 'No transactions found for this personnel.')}
          columnResizeStorageKey="personnelActions"
          onRowClick={(row) => setTransactionForDetails(row)}
          sx={{ mt: 2 }}
        />

        {/* Genel Toplam özeti (eski tablo alt toplam satırının karşılığı) */}
        {displayedTransactions.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: { xs: 1, sm: 3 }, mt: 1.5, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {tr('Genel Toplam', 'Grand Total')}:
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
              {tr('Şirket Alacağı', 'Company Receivable')}: {new Intl.NumberFormat(isEnglish ? 'en-US' : 'tr-TR', { style: 'currency', currency: 'TRY' }).format(totalDebit)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
              {tr('Şirket Borcu', 'Company Payable')}: {new Intl.NumberFormat(isEnglish ? 'en-US' : 'tr-TR', { style: 'currency', currency: 'TRY' }).format(totalCredit)}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Modallar */}
      {personnelId && personnelName && userDetails?.companyId && (
        <>
          {modalSourceAction && (
            <>
              <AddReceivableModal 
                open={openAddReceivableModal}
                onClose={() => setOpenAddReceivableModal(false)}
                personnelId={personnelId}
                personnelName={personnelName}
                companyId={userDetails.companyId}
                sourceAction={modalSourceAction}
                {...modalScopeProps}
                onSuccess={() => {
                  fetchPersonnelTransactions(); // Hareket listesini yenile
                  setOpenAddReceivableModal(false);
                }}
              />
              <AddDebtModal 
                open={openAddDebtModal}
                onClose={() => setOpenAddDebtModal(false)}
                personnelId={personnelId}
                personnelName={personnelName}
                companyId={userDetails.companyId}
                sourceAction={modalSourceAction}
                initialPaymentDescription={paymentDescription}
                transactionKind={paymentKind}
                {...modalScopeProps}
                onSuccess={() => {
                  fetchPersonnelTransactions(); // Hareket listesini yenile
                  setOpenAddDebtModal(false);
                }}
              />
            </>
          )}
          
          {/* Maaş/Prim modal'ı ayrı olarak render ediliyor */}
          <SalaryBonusModal
            open={openSalaryBonusModal}
            onClose={() => setOpenSalaryBonusModal(false)}
            personnelId={personnelId || ''}
            personnelName={personnelName || ''}
            companyId={userDetails?.companyId || null}
            onSuccess={fetchPersonnelTransactions}
            initialTab={salaryBonusType || 'salary'}
            {...modalScopeProps}
          />
          
          {/* Yeni Ödeme Modalı */}
          <PaymentModal
            open={openPaymentModal}
            onClose={() => setOpenPaymentModal(false)}
            personnelId={personnelId || ''}
            personnelName={personnelName || ''}
            companyId={userDetails?.companyId || null}
            initialTab={paymentModalType === 'bonus' ? 'bonus' : 'salary'}
            onSuccess={fetchPersonnelTransactions}
            {...modalScopeProps}
          />
        </>
      )}

      {/* Tekil hareket detayı — satıra tıklayınca açılır (cari ekstresiyle aynı desen) */}
      <Dialog
        open={!!transactionForDetails}
        onClose={() => setTransactionForDetails(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{tr('Hareket Detayı', 'Transaction Details')}</DialogTitle>
        <DialogContent>
          {transactionForDetails && (
            <Paper variant="outlined" sx={{ p: 2, mt: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tr('İşlem Tarihi', 'Transaction Date')}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatDateTime(transactionForDetails.createdAt)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tr('İşlem Türü', 'Transaction Type')}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {transactionTypeLabel(transactionForDetails.transactionType)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tr('Ödeme Tipi', 'Payment Method')}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {paymentMethodLabel(transactionForDetails.paymentMethod)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tr('Banka/Kasa', 'Bank/Cashbox')}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {bankOrCashLabel(transactionForDetails)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tr('Açıklama', 'Description')}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {transactionForDetails.description || '-'}
                  </Typography>
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tr('Tutar', 'Amount')}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatAmount(transactionForDetails.amount, transactionForDetails.currency)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tr('İşlem Yönü', 'Direction')}
                  </Typography>
                  <Chip
                    label={isCompanyReceivable(transactionForDetails.transactionType)
                      ? tr('Şirket Alacağı', 'Company Receivable')
                      : tr('Şirket Borcu', 'Company Payable')}
                    size="small"
                    color={isCompanyReceivable(transactionForDetails.transactionType) ? 'success' : 'error'}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tr('Önceki Bakiye', 'Previous Balance')}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {formatAmount(transactionForDetails.previousBalance ?? 0, transactionForDetails.currency)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tr('Sonraki Bakiye', 'Next Balance')}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {/* Son hareketse "sonraki" yoktur; işlem sonrası bakiye gösterilir. */}
                    {formatAmount(transactionForDetails.nextBalance ?? transactionForDetails.balance ?? 0, transactionForDetails.currency)}
                  </Typography>
                </Grid>

                <Grid item xs={12}><Divider /></Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {tr('İşlemi Yapan', 'Processed By')}
                  </Typography>
                  <Typography variant="body2" fontWeight="medium">
                    {transactionForDetails.performedBy || '-'}
                  </Typography>
                </Grid>

                {transactionForDetails.isAccrual && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {tr('Kayıt Tipi', 'Record Type')}
                    </Typography>
                    <Chip label={tr('Tahakkuk (para hareketi yok)', 'Accrual (no cash movement)')} size="small" variant="outlined" />
                  </Grid>
                )}
              </Grid>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setTransactionForDetails(null)} variant="contained">
            {tr('Kapat', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hareket Silme Onay Modalı (bağlı kasa/banka hareketini de geri alır) */}
      <PersonnelTransactionDeleteConfirmModal
        open={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setTransactionToDelete(null); }}
        transaction={transactionToDelete}
        onDeleted={fetchPersonnelTransactions}
      />
    </>
  );
};

export default PersonnelActions; 